import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDomains, useProjects } from '@/lib/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, ChevronRight } from 'lucide-react';
import ProjectForm from '@/components/ProjectForm';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import type { Project } from '@/lib/types';

const COLUMNS: Array<{
  status: Project['status'];
  label: string;
  tone: 'planned' | 'active' | 'blocked' | 'done';
}> = [
  { status: 'planned', label: 'Planned', tone: 'planned' },
  { status: 'active', label: 'Active', tone: 'active' },
  { status: 'blocked', label: 'Blocked', tone: 'blocked' },
  { status: 'completed', label: 'Completed', tone: 'done' },
];

export default function Projects() {
  const all = useProjects({});
  const domains = useDomains();
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const byStatus = new Map<Project['status'], Project[]>();
  for (const p of all.data ?? []) {
    const list = byStatus.get(p.status) ?? [];
    list.push(p);
    byStatus.set(p.status, list);
  }

  return (
    <PullToRefresh
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ['projects'] })}
    >
      <div className="space-y-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Workbench · Projects
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              Projects
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              Non-recurring work — clear the garage, repair the porch shed, install shelving.
              Status board on the left, drill into a card for full detail.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} className="h-11 self-start sm:self-auto" data-testid="new-project">
            <Plus className="h-4 w-4 mr-2" aria-hidden /> New project
          </Button>
        </header>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {COLUMNS.map((col) => {
            const items = byStatus.get(col.status) ?? [];
            return (
              <section key={col.status} className="space-y-2" aria-labelledby={`col-${col.status}`}>
                <div className="flex items-baseline justify-between border-b border-rule pb-2">
                  <h2
                    id={`col-${col.status}`}
                    className="font-display text-sm font-semibold tracking-tight"
                  >
                    {col.label}
                  </h2>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground tabular-nums">
                    {items.length}
                  </span>
                </div>
                <ul className="space-y-2">
                  {items.length === 0 ? (
                    <li className="text-xs text-muted-foreground italic px-1">—</li>
                  ) : null}
                  {items.map((p) => {
                    const dom = (domains.data ?? []).find((d) => d.id === p.domainId);
                    return (
                      <li key={p.id}>
                        <Link
                          to={`/projects/${p.id}`}
                          className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
                          data-testid={`project-card-${p.id}`}
                        >
                          <Card
                            className={cn(
                              'transition-all border-rule group-hover:border-primary/30 group-hover:shadow-md',
                              p.priority === 'urgent' && 'border-warning/40',
                            )}
                          >
                            <CardHeader className="pb-2 space-y-1">
                              {dom ? (
                                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                  {dom.slug}
                                </p>
                              ) : null}
                              <div className="flex items-start justify-between gap-2">
                                <CardTitle className="font-display text-sm leading-tight">
                                  {p.title}
                                </CardTitle>
                                <ChevronRight
                                  className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground group-hover:text-primary"
                                  aria-hidden
                                />
                              </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <Badge
                                variant={p.priority === 'urgent' ? 'destructive' : 'secondary'}
                                className="font-mono text-[10px] tracking-wider"
                              >
                                {p.priority}
                              </Badge>
                            </CardContent>
                          </Card>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>

        <ProjectForm open={creating} onOpenChange={setCreating} mode="create" />
      </div>
    </PullToRefresh>
  );
}
